import { AppConfig, AppConfigData, StaticConfigData, DynamicConfigData, SettingsValueMap, SettingType, DynamicSetting } from '@/types/config';
import { defaultConfig } from '@/config/defaults';

class ConfigService {
  private config: AppConfig = defaultConfig;
  private isLoading = false;
  private error: string | null = null;

  private configUrls = {
    app: '/config/app-config.json',
    static: '/config/static-config.json',
    dynamic: '/config/dynamic-config.json'
  };

  /**
   * Load all configuration files and merge them
   */
  async loadConfig(): Promise<AppConfig> {
    this.isLoading = true;
    this.error = null;

    try {
      // Load all three configuration files in parallel
      const [appConfig, staticConfig, dynamicConfig] = await Promise.all([
        this.loadConfigFile<AppConfigData>(this.configUrls.app),
        this.loadConfigFile<StaticConfigData>(this.configUrls.static),
        this.loadConfigFile<DynamicConfigData>(this.configUrls.dynamic)
      ]);

      // Merge configurations with defaults
      this.config = this.mergeConfigurations(appConfig, staticConfig, dynamicConfig);

      return this.config;
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load configuration';
      console.error('❌ Config loading error:', err);
      console.warn('⚠️ Using default configuration as fallback');
      return this.config;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load a single configuration file
   */
  private async loadConfigFile<T>(url: string): Promise<T> {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load ${url}: ${response.status} ${response.statusText}`);
      }

      const config = await response.json();
      return config;
    } catch (error) {
      console.error(`❌ Error loading ${url}:`, error);
      return {} as T;
    }
  }

  /**
   * Merge configurations from multiple sources - completely dynamic
   */
  private mergeConfigurations(
    appConfig: AppConfigData,
    staticConfig: StaticConfigData,
    dynamicConfig: DynamicConfigData
  ): AppConfig {
    return {
      // App Configuration
      metadata: { ...defaultConfig.metadata, ...appConfig.metadata },
      pages: { ...defaultConfig.pages, ...appConfig.pages },

      // Static Configuration
      layout: { ...defaultConfig.layout, ...staticConfig },

      // Dynamic Configuration - merge everything dynamically
      ...dynamicConfig
    };
  }

  /**
   * Get current configuration
   */
  getConfig(): AppConfig {
    return this.config;
  }

  /**
   * Get loading state
   */
  getLoadingState(): { isLoading: boolean; error: string | null } {
    return { isLoading: this.isLoading, error: this.error };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AppConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Validate configuration structure
   */
  validateConfig(config: AppConfig): boolean {
    // Basic validation - check for required fields
    const requiredFields = ['metadata', 'pages'];

    for (const field of requiredFields) {
      if (!config[field as keyof AppConfig]) {
        console.error(`Missing required config field: ${field}`);
        return false;
      }
    }

    return true;
  }

  /**
   * Generate settings value map from configuration - completely dynamic
   * Creates a flat map of {[key: string]: boolean} for session storage
   * 
   * For TOGGLE: {[settingsName]: boolean}
   * For SELECT: {[selectName]: boolean}
   */
  generateSettingsValueMap(): SettingsValueMap {
    const valueMap: SettingsValueMap = {};

    // Process settings panel if it exists
    this.processSettingsPanel(this.config, valueMap);

    return valueMap;
  }

  /**
   * Process settings panel structure - completely dynamic
   */
  private processSettingsPanel(obj: unknown, valueMap: SettingsValueMap): void {
    if (!obj || typeof obj !== 'object') return;

    // Look for settingsPanel in the object
    if ('settingsPanel' in obj && obj.settingsPanel && typeof obj.settingsPanel === 'object') {
      const settingsPanel = obj.settingsPanel as Record<string, unknown>;

      // Process each tab in settingsPanel
      Object.entries(settingsPanel).forEach(([tabName, tab]) => {
        this.processSettingsTab(tabName, tab, valueMap);
      });
    }
  }

  /**
   * Process a settings tab - completely dynamic
   */
  private processSettingsTab(tabName: string, tab: unknown, valueMap: SettingsValueMap): void {
    if (!tab || typeof tab !== 'object') return;

    // Check if tab is visible (optional)
    const isVisible = 'visible' in tab ? tab.visible : true;
    if (isVisible === false) {
      return;
    }

    // Process each setting in the tab
    Object.entries(tab).forEach(([settingName, setting]) => {
      if (settingName === 'visible' || settingName === 'label') return; // Skip visibility and label properties

      this.processSetting(settingName, setting, valueMap);
    });
  }

  /**
   * Process a single setting - completely dynamic
   */
  private processSetting(settingName: string, setting: unknown, valueMap: SettingsValueMap): void {
    if (!setting || typeof setting !== 'object' || !('type' in setting)) {
      return;
    }

    const typedSetting = setting as DynamicSetting;

    switch (typedSetting.type) {
      case SettingType.TOGGLE:
        // For TOGGLE: {[settingsName]: boolean}
        valueMap[settingName] = typedSetting.defaultValue ?? false;
        break;

      case SettingType.SELECT:
        // For SELECT: {[selectName]: boolean}
        if (typedSetting.data) {
          Object.entries(typedSetting.data).forEach(([selectName, option]) => {
            valueMap[selectName] = option.value ?? false;
          });
        }
        break;

      default:
        console.warn(`⚠️ Unknown setting type for ${settingName}:`, typedSetting.type);
    }
  }

  /**
   * Get a specific setting value by key
   */
  getSettingValue(key: string): boolean | undefined {
    // First check session storage for the current value
    const storedValueMap = this.checkAndMapStorageData();
    const storedValue = storedValueMap[key];
    return storedValue;
  }

  /**
   * Check if a setting is enabled/true
   */
  isSettingEnabled(key: string): boolean {
    const value = this.getSettingValue(key); return value === true;
  }

  /**
   * Get all visible tabs from settings panel - completely dynamic
   */
  getVisibleTabs(): string[] {
    const settingsPanel = this.config.settingsPanel as Record<string, { visible?: boolean }>;
    if (!settingsPanel) return [];

    return Object.entries(settingsPanel)
      .filter(([_, tab]) => tab && typeof tab === 'object' && tab.visible !== false)
      .map(([tabName, _]) => tabName);
  }

  /**
   * Get tab labels with their names - completely dynamic
   */
  getTabLabels(): Array<{ name: string; label: string }> {
    const settingsPanel = this.config.settingsPanel as Record<string, { visible?: boolean; label?: string }>;
    if (!settingsPanel) return [];

    return Object.entries(settingsPanel)
      .filter(([_, tab]) => tab && typeof tab === 'object' && tab.visible !== false)
      .map(([tabName, tab]) => ({
        name: tabName,
        label: tab.label || tabName.charAt(0).toUpperCase() + tabName.slice(1) // Fallback to capitalized name
      }));
  }

  /**
   * Get label for a specific tab - completely dynamic
   */
  getTabLabel(tabName: string): string {
    const settingsPanel = this.config.settingsPanel as Record<string, { visible?: boolean; label?: string }>;
    const tab = settingsPanel?.[tabName];

    if (tab && typeof tab === 'object' && tab.label) {
      return tab.label;
    }

    // Fallback to capitalized tab name
    return tabName.charAt(0).toUpperCase() + tabName.slice(1);
  }

  /**
   * Get all settings for a specific tab - completely dynamic
   */
  getTabSettings(tabName: string): Record<string, unknown> {
    const settingsPanel = this.config.settingsPanel as Record<string, Record<string, unknown>>;
    const tab = settingsPanel?.[tabName];
    if (!tab || typeof tab === 'boolean') return {};

    const settings: Record<string, unknown> = {};
    Object.entries(tab).forEach(([key, value]) => {
      if (key !== 'visible' && typeof value === 'object' && value !== null) {
        settings[key] = value;
      }
    });

    return settings;
  }

  /**
   * Get any value from config using dot notation - completely dynamic
   */
  getConfigValue(path: string): unknown {
    return path.split('.').reduce((obj, key) => {
      if (obj && typeof obj === 'object' && key in obj) {
        return (obj as Record<string, unknown>)[key];
      }
      return undefined;
    }, this.config as unknown);
  }

  /**
   * Check if any feature is enabled using dot notation - completely dynamic
   */
  isFeatureEnabled(path: string): boolean {
    const value = this.getConfigValue(path);
    return Boolean(value);
  }

  /**
   * Save settings value map to session storage
   */
  saveSettingsToStorage(): void {
    try {
      const valueMap = this.generateSettingsValueMap();
      const storageKey = 'config-settings-value-map';

      sessionStorage.setItem(storageKey, JSON.stringify(valueMap));

    } catch (error) {
      console.error('❌ Failed to save settings to session storage:', error);
    }
  }

  /**
   * Load settings value map from session storage
   */
  loadSettingsFromStorage(): SettingsValueMap {
    try {
      const storageKey = 'config-settings-value-map';
      const saved = sessionStorage.getItem(storageKey);

      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }

      return this.generateSettingsValueMap();

    } catch (error) {
      console.error('❌ Failed to load settings from session storage:', error);
      return this.generateSettingsValueMap();
    }
  }

  /**
   * Clear settings from session storage
   */
  clearSettingsFromStorage(): void {
    try {
      const storageKey = 'config-settings-value-map';
      sessionStorage.removeItem(storageKey);

    } catch (error) {
      console.error('❌ Failed to clear settings from session storage:', error);
    }
  }

  /**
   * Check if session storage has existing data and map it to current config
   */
  checkAndMapStorageData(): SettingsValueMap {
    try {
      const storageKey = 'config-settings-value-map';
      const saved = sessionStorage.getItem(storageKey);

      if (saved) {
        const parsed = JSON.parse(saved);

        // Validate that the stored data is a valid settings map
        if (this.isValidSettingsMap(parsed)) {
          return parsed;
        } else {
          return this.generateSettingsValueMap();
        }
      }
      return this.generateSettingsValueMap();

    } catch (error) {
      console.error('❌ Error checking session storage data:', error);
      return this.generateSettingsValueMap();
    }
  }

  /**
   * Validate if the stored data is a valid settings value map
   */
  private isValidSettingsMap(data: unknown): data is SettingsValueMap {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Check if all values are boolean
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return true; // Empty object is valid
    }

    for (const [key, value] of entries) {
      if (typeof key !== 'string') {
        return false;
      }
      if (typeof value !== 'boolean') {
        return false;
      }
    }

    return true;
  }

  /**
   * Map stored settings back to config structure (for display/debugging)
   */
  mapStorageToConfig(storedData: SettingsValueMap): Record<string, unknown> {
    const mappedConfig: Record<string, unknown> = {};

    // Get current config structure to understand what settings should exist
    const currentSettingsMap = this.generateSettingsValueMap();

    // Create a mapping showing which stored values correspond to which settings
    const mapping: Record<string, { stored: boolean; current: boolean; exists: boolean }> = {};

    Object.entries(storedData).forEach(([key, storedValue]) => {
      const currentValue = currentSettingsMap[key];
      const exists = key in currentSettingsMap;

      mapping[key] = {
        stored: storedValue,
        current: currentValue,
        exists
      };
    });

    // Add any current settings that don't exist in storage
    Object.entries(currentSettingsMap).forEach(([key, currentValue]) => {
      if (!(key in storedData)) {
        mapping[key] = {
          stored: false,
          current: currentValue,
          exists: true
        };
      }
    });

    // Create a summary of the mapping
    mappedConfig.mapping = mapping;
    mappedConfig.storedData = storedData;
    mappedConfig.currentStructure = currentSettingsMap;
    mappedConfig.summary = {
      totalStored: Object.keys(storedData).length,
      totalCurrent: Object.keys(currentSettingsMap).length,
      matchingKeys: Object.keys(storedData).filter(key => key in currentSettingsMap).length,
      newKeys: Object.keys(currentSettingsMap).filter(key => !(key in storedData)).length,
      obsoleteKeys: Object.keys(storedData).filter(key => !(key in currentSettingsMap)).length
    };

    return mappedConfig;
  }

  /**
   * Get page configuration for a specific route
   */
  getPageConfig(pageRoute: string): AppConfigData['pages'][string] | undefined {
    return this.config.pages?.[pageRoute];
  }

  /**
   * Get all available page routes
   */
  getPageRoutes(): string[] {
    return Object.keys(this.config.pages || {});
  }

  /**
   * Check if a page route exists
   */
  hasPageRoute(pageRoute: string): boolean {
    return pageRoute in (this.config.pages || {});
  }

  /**
   * Get page metadata (name, subTitle, etc.)
   */
  getPageMetadata(pageRoute: string): { name: string; subTitle: string } | undefined {
    const pageConfig = this.getPageConfig(pageRoute);
    if (!pageConfig) return undefined;
    
    return {
      name: pageConfig.name,
      subTitle: pageConfig.subTitle
    };
  }

  /**
   * Get page agent label
   */
  getPageAgentLabel(pageRoute: string): string | undefined {
    const pageConfig = this.getPageConfig(pageRoute);
    return pageConfig?.agentLabel;
  }

  /**
   * Get page info data (header, subheader, list)
   */
  getPageInfo(pageRoute: string): {
    header: string;
    subHeader: string;
    list: Array<{
      title: string;
      list: Array<{
        header: string;
        context: string;
      }>;
    }>;
  } | undefined {
    const pageConfig = this.getPageConfig(pageRoute);
    if (!pageConfig) return undefined;
    
    return {
      header: pageConfig.infoHeader,
      subHeader: pageConfig.infoSubHeader,
      list: pageConfig.infoList
    };
  }
}

export const configService = new ConfigService(); 